// ─── Risk state machine ────────────────────────────────────────────────────

export type NoShowRiskMachineState = 'IDLE' | 'AWAITING_SCORES' | 'RISK_ACTIVE';

// ─── Core types ────────────────────────────────────────────────────────────

export type RiskLevel = 'low' | 'moderate' | 'high' | 'critical';

export interface ReminderTiming {
  hoursBeforeAppointment: number;
  channel: 'sms' | 'email' | 'phone' | 'any';
}

export interface ReminderTier {
  reminders: ReminderTiming[];
  requireConfirmation: boolean;
  flagForOutreach: boolean;
}

export interface ReminderScheduleConfig {
  low: ReminderTier;
  moderate: ReminderTier;
  high: ReminderTier;
  critical: ReminderTier;
}

export const DEFAULT_REMINDER_SCHEDULE: ReminderScheduleConfig = {
  low: {
    reminders: [{ hoursBeforeAppointment: 24, channel: 'any' }],
    requireConfirmation: false,
    flagForOutreach: false,
  },
  moderate: {
    reminders: [
      { hoursBeforeAppointment: 72, channel: 'any' },
      { hoursBeforeAppointment: 24, channel: 'any' },
    ],
    requireConfirmation: true,
    flagForOutreach: false,
  },
  high: {
    reminders: [
      { hoursBeforeAppointment: 120, channel: 'any' },
      { hoursBeforeAppointment: 48,  channel: 'any' },
      { hoursBeforeAppointment: 24,  channel: 'any' },
    ],
    requireConfirmation: true,
    flagForOutreach: true,
  },
  critical: {
    reminders: [
      { hoursBeforeAppointment: 168, channel: 'phone' },
      { hoursBeforeAppointment: 72,  channel: 'phone' },
      { hoursBeforeAppointment: 24,  channel: 'sms' },
      { hoursBeforeAppointment: 2,   channel: 'sms' },
    ],
    requireConfirmation: true,
    flagForOutreach: true,
  },
};

// ─── Feature config (top-level prop) ──────────────────────────────────────

export interface NoShowRiskConfig {
  enabled: boolean;
  /**
   * Fired when the component needs risk scores for the current view.
   * Iframe mode: host replies with a RISK_SCORES postMessage.
   * Direct-React mode: return Promise<RiskScoresPayload> to apply scores immediately.
   */
  onRequestRiskScores: (payload: RiskScoresRequestPayload) => void | Promise<RiskScoresPayload>;
  onReminderDue: (payload: ReminderDuePayload) => void;
  onReminderResponse?: (payload: AppointmentStatusUpdatePayload) => void;
  onRiskCommand?: (payload: RiskCommandPayload) => void;
  reminderSchedule?: ReminderScheduleConfig;
}

// ─── Outbound payloads (component → host) ─────────────────────────────────

export interface AppointmentSummary {
  appointmentId: string;
  patientId: string;
  resourceId: string;
  start: string;
  end: string;
  appointmentType: string;
  screenerSeverity?: 'low' | 'moderate' | 'high' | 'crisis';
  hasBackfillCandidate?: boolean;
  bookedFromAcuityReservation?: boolean;
}

export interface RiskScoresRequestPayload {
  visibleRangeStart: string;
  visibleRangeEnd: string;
  appointments: AppointmentSummary[];
}

export interface ReminderDuePayload {
  appointmentId: string;
  patientId: string;
  resourceId: string;
  appointmentStart: string;
  riskLevel: RiskLevel;
  riskScore: number;
  reminderNumber: number;
  totalReminders: number;
  channel: 'sms' | 'email' | 'phone' | 'any';
  requireConfirmation: boolean;
  flagForOutreach: boolean;
  hoursUntilAppointment: number;
}

export interface OutreachFlaggedPayload {
  appointmentId: string;
  patientId: string;
  resourceId: string;
  appointmentStart: string;
  riskLevel: RiskLevel;
  riskScore: number;
  riskFactors: string[];
  remindersSentCount: number;
  confirmed: boolean;
  acuityLevel?: string;
  matchLevel?: 'strong' | 'partial' | 'weak';
}

export interface RiskCommandPayload {
  intent: 'QUERY' | 'SUPPRESS' | 'OVERRIDE_SCHEDULE' | 'FLAG_OUTREACH' | 'RESEND';
  appointmentId?: string;
  resourceId?: string;
  dateRange?: { start: string; end: string };
  overrideSchedule?: ReminderScheduleConfig;
  raw: string;
}

// ─── Inbound payloads (host → component) ──────────────────────────────────

export interface AppointmentRiskScore {
  appointmentId: string;
  riskLevel: RiskLevel;
  riskScore: number;
  riskFactors: string[];
  reminderScheduleOverride?: ReminderScheduleConfig;
  suppressReminders?: boolean;
}

export interface RiskScoresPayload {
  scores: AppointmentRiskScore[];
}

export interface ReminderOutcomePayload {
  appointmentId: string;
  reminderNumber: number;
  delivered: boolean;
  channel: 'sms' | 'email' | 'phone';
  deliveredAt: string;
  failureReason?: string;
}

export interface AppointmentStatusUpdatePayload {
  appointmentId: string;
  status: 'confirmed' | 'rescheduled' | 'cancelled' | 'no_response';
  respondedAt?: string;
  newAppointmentId?: string;
}

// ─── Internal scheduler state ──────────────────────────────────────────────

export type ReminderDeliveryStatus = 'scheduled' | 'due' | 'delivered' | 'failed';

export interface AppointmentReminderState {
  appointmentId: string;
  patientId: string;
  resourceId: string;
  appointmentStart: string;
  riskLevel: RiskLevel;
  riskScore: number;
  riskFactors: string[];
  suppressReminders: boolean;
  schedule: ReminderTiming[];
  requireConfirmation: boolean;
  flagForOutreach: boolean;
  firedReminders: number[];
  outreachFlagged: boolean;
  status: 'pending' | 'confirmed' | 'rescheduled' | 'cancelled';
  lastReminderOutcome?: ReminderDeliveryStatus;
  lastDeliveredAt?: string;
}

// ─── Hook return type ──────────────────────────────────────────────────────

export interface NoShowRiskHandle {
  enabled: boolean;
  machineState: NoShowRiskMachineState;
  riskScoresMap: Map<string, AppointmentRiskScore>;
  getReminderState: (appointmentId: string) => AppointmentReminderState | undefined;
  requestRiskScores: () => void;
  onRiskScoresReceived: (payload: RiskScoresPayload) => void;
  onReminderOutcomeReceived: (payload: ReminderOutcomePayload) => void;
  onAppointmentStatusUpdateReceived: (payload: AppointmentStatusUpdatePayload) => void;
  handleRiskCommand: (payload: RiskCommandPayload) => void;
}
